import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import ComputerIcon from '@mui/icons-material/Computer';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import TerminalIcon from '@mui/icons-material/Terminal';

import { downloadBlob, downloadPdvInstalador, getPdvInstaladores } from '../services/api';

const ICONES = {
  windows: <ComputerIcon color="primary" />,
  linux: <TerminalIcon color="primary" />,
  android: <PhoneAndroidIcon color="primary" />,
  android_pos: <PhoneAndroidIcon color="secondary" />,
};

function descricaoItem(item) {
  if (item.delivery === 'stone_partner_hub') {
    return 'Publicado no Stone Partner Hub — sem download direto.';
  }
  if (!item.disponivel) {
    return 'Artefato ainda não publicado para esta plataforma.';
  }
  const partes = [];
  if (item.version) partes.push(`v${item.version}`);
  if (item.build) partes.push(`build ${item.build}`);
  if (item.nomeArquivo) partes.push(item.nomeArquivo);
  return partes.join(' · ') || 'Disponível para download';
}

export default function PdvInstaladoresDialog({ open, onClose }) {
  const [itens, setItens] = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState('');
  const [baixando, setBaixando] = useState('');

  const carregar = useCallback(async () => {
    try {
      setCarregando(true);
      setErro('');
      const { data } = await getPdvInstaladores();
      setItens(Array.isArray(data) ? data : []);
    } catch {
      setErro('Não foi possível carregar os instaladores do PDV.');
      setItens([]);
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    if (open) carregar();
  }, [open, carregar]);

  const baixar = async (item) => {
    if (!item.disponivel || item.delivery === 'stone_partner_hub') return;
    try {
      setBaixando(item.plataforma);
      const { blob, nomeArquivo } = await downloadPdvInstalador(item.plataforma, item.build);
      downloadBlob(blob, nomeArquivo || item.nomeArquivo || `zenpdv-${item.plataforma}`);
    } catch {
      setErro(`Falha ao baixar instalador para ${item.label}.`);
    } finally {
      setBaixando('');
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Baixar instaladores do ZenPDV</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Use estes pacotes para instalar o PDV em um terminal novo. O auto-update dentro do app
          continua usando ZIP (Windows/Linux) ou APK (Android).
        </Typography>

        {erro ? <Alert severity="error" sx={{ mb: 2 }}>{erro}</Alert> : null}

        {carregando ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : itens.length === 0 ? (
          <Typography color="text.secondary">Nenhum instalador configurado no servidor.</Typography>
        ) : (
          <List disablePadding>
            {itens.map((item) => {
              const podeBaixar = item.disponivel && item.delivery !== 'stone_partner_hub';
              return (
                <ListItem
                  key={item.plataforma}
                  secondaryAction={
                    podeBaixar ? (
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={
                          baixando === item.plataforma
                            ? <CircularProgress size={16} />
                            : <DownloadIcon />
                        }
                        disabled={!!baixando}
                        onClick={() => baixar(item)}
                      >
                        Baixar
                      </Button>
                    ) : null
                  }
                  sx={{ px: 0 }}
                >
                  <Box sx={{ mr: 1.5, display: 'flex', alignItems: 'center' }}>
                    {ICONES[item.plataforma] || <ComputerIcon color="disabled" />}
                  </Box>
                  <ListItemText
                    primary={item.label}
                    secondary={descricaoItem(item)}
                  />
                </ListItem>
              );
            })}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Fechar</Button>
      </DialogActions>
    </Dialog>
  );
}
